<template>
  <div ref="rootRef" class="tw-w-full tw-relative" :class="largeWidth ? 'tw-max-w-lg' : ''">
    <label v-if="label" :for="id"
      class="tw-block tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">
      {{ label }}
    </label>

    <input ref="inputRef" :id="id" type="text" :placeholder="placeholder" :value="modelValue" @input="handleInput"
      @keydown.enter="handleEnter"
      @keydown.esc="clearSuggestions"
      :autocomplete="autocomplete"
      class="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55" />
    <ul v-if="suggestions.length"
      class="tw-absolute tw-z-50 tw-mt-1 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-lg tw-max-h-60 tw-overflow-y-auto">
      <li v-for="(s, index) in suggestions" :key="index" @click="selectSuggestion(index)"
        class="tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-slate-50">
        {{ s.displayText }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref } from "vue";

export default defineComponent({
  name: "GoogleAddressInput",
  props: {
    id: String,
    placeholder: { type: String, default: "Enter address..." },
    modelValue: [String, Number],
    label: { type: [String, Number], default: undefined },
    largeWidth: { type: Boolean, default: false },
    autocomplete: { type: String, default: "off" },
  },
  emits: ["update:modelValue", "address-selected"],

  setup(props, { emit }) {
    const inputRef = ref<HTMLInputElement | null>(null);
    const rootRef = ref<HTMLElement | null>(null);

    const suggestions = ref<{ displayText: string }[]>([]);

    let rawPredictions: any[] = [];
    let AutocompleteSuggestion: any;

    const clearSuggestions = () => {
      suggestions.value = [];
      rawPredictions = [];
    };

    const handleOutsideClick = (event: MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode) return;
      if (rootRef.value && !rootRef.value.contains(targetNode)) {
        clearSuggestions();
      }
    };

    onMounted(async () => {
      const g = (window as any).google;
      if (!g?.maps) return;

      const placesLib = await g.maps.importLibrary("places");
      AutocompleteSuggestion = placesLib.AutocompleteSuggestion;

      document.addEventListener("mousedown", handleOutsideClick);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", handleOutsideClick);
    });

    let timeout: any;
    const handleInput = async (e: any) => {
      const value = e.target.value;
      emit("update:modelValue", value);

      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        if (!value || !AutocompleteSuggestion) {
          clearSuggestions();
          return;
        }

        try {
          const response =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input: value,
              includedRegionCodes: ["us", "ca"],
            });

          rawPredictions = response.suggestions || [];

          suggestions.value = rawPredictions.map((p: any) => ({
            displayText: p.placePrediction?.text?.text || "Unknown address",
          }));
        } catch (err) {
          console.error("Autocomplete error:", err);
          clearSuggestions();
        }
      }, 300);
    };

    const handleEnter = (e: KeyboardEvent) => {
      // Keep manual typing uninterrupted: Enter closes suggestions.
      e.preventDefault();
      clearSuggestions();
    };

    const selectSuggestion = async (index: number) => {
      const prediction = rawPredictions[index];
      if (!prediction) return;

      suggestions.value = [];

      const place = prediction.placePrediction.toPlace();

      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });

      let streetNumber = "";
      let route = "";
      let city = "";
      let state = "";
      let zip = "";

      place.addressComponents?.forEach((c: any) => {
        if (c.types.includes("street_number")) streetNumber = c.longText;
        if (c.types.includes("route")) route = c.longText;
        if (c.types.includes("locality")) city = c.longText;
        if (c.types.includes("administrative_area_level_1"))
          state = c.shortText;
        if (c.types.includes("postal_code")) zip = c.longText;
      });

      const streetAddress = `${streetNumber} ${route}`.trim();

      if (inputRef.value) inputRef.value.value = streetAddress;

      emit("update:modelValue", streetAddress);
      emit("address-selected", {
        streetAddress,
        city,
        state,
        zipCode: zip,
      });
    };

    return {
      inputRef,
      rootRef,
      suggestions,
      clearSuggestions,
      handleEnter,
      handleInput,
      selectSuggestion,
    };
  },
});
</script>
